import * as React from 'react'
import { useCallback } from 'react'
import { Theme } from '@mui/material/styles'

import { CalendarDatePicker } from '@user/components/CalendarButton'
import {
  useUpdateOpportunityExpectedClosingDate,
  useUpdateOpportunityNextActionDate,
} from '@user/store/opportunity'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { ENTITY_TYPES } from '@sales-center/common'
import { useUpdateLeadNextActionDate } from '@user/store/lead'
import { useData } from './hooks'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import { setEntityNextActionDate } from '@user/store/map'
import { useAppDispatch } from '@user/store/hooks'

const styles = {
  root: {
    marginBottom: (theme: Theme) => theme.spacing(1),
  },

  nextActionDateLabel: {
    fontFamily: 'Source Sans Pro Semibold',
  },
}

interface Props {
  id: number
  nextActionDate: Date | null
  disabled: boolean
  entity?: ENTITY_TYPES
  containerStyles?: React.CSSProperties
  updateEntityDetails?: boolean
  noTitle?: boolean
  textOnly?: boolean
}

export function NextActionDate(props: Props) {
  const {
    id,
    nextActionDate,
    disabled,
    entity,
    containerStyles,
    updateEntityDetails,
    noTitle = false,
    textOnly = false,
  } = props
  const dispatch = useAppDispatch()
  const { execute: updateOpportunityNextActionDate } =
    useUpdateOpportunityNextActionDate()

  const { execute: updateOpportunityExpectedClosingDate } =
    useUpdateOpportunityExpectedClosingDate()
  const { execute: updateLeadNextActionDate } = useUpdateLeadNextActionDate()

  const handleNextActionDateChange = useCallback(
    (date: Date | null) => {
      if (entity === ENTITY_TYPES.Lead) {
        updateLeadNextActionDate({
          leadId: id,
          nextActionDate: date,
        })
      } else {
        updateOpportunityNextActionDate({
          opportunityId: id,
          nextActionDate: date,
        })
      }
      if (updateEntityDetails) dispatch(setEntityNextActionDate(date))
    },
    [
      dispatch,
      entity,
      id,
      updateEntityDetails,
      updateLeadNextActionDate,
      updateOpportunityNextActionDate,
    ],
  )

  const { canUpdate } = useData({ entity })

  const { getFormat } = useLocalizationDateFormat()

  return (
    <Box sx={containerStyles}>
      {!noTitle && (
        <Typography sx={styles.nextActionDateLabel} component="div">
          Next Action Date
        </Typography>
      )}

      <CalendarDatePicker
        canUpdate={canUpdate}
        clearable={canUpdate}
        disablePast={false}
        disabled={disabled}
        value={nextActionDate}
        onAccept={handleNextActionDateChange}
        datePickerType="nextaction"
        updateOtherDateCallback={updateOpportunityExpectedClosingDate}
        entityType={entity}
        id={id}
        format={getFormat()}
        dataTestId={`next-action-date-${id}`}
        textOnly={textOnly}
      />
    </Box>
  )
}
